/**
 * Onboarding request handler using EIP-712 signing
 * Handles signing and HTTP request to /auth/v1/onboard
 *
 * CRITICAL: The router expects EIP-712 signature with ECDSA_LEGACY format.
 * The Go backend's GenerateRouterWriteSignature uses EIP-712 structured hashing.
 *
 * Message structure: action, owner, nonce, timestamp, deadline
 * All fields must be included in the EIP-712 signed payload.
 */

import { ethers } from "ethers";
import { HttpClient } from "../transport/http/HttpClient";
import { OperationTypeOnboard } from "../types/operations";
import mormSigner from "./mormSigner";

export interface OnboardRequestParams {
  mnemonic: string;
  walletAddress: string;
  message: {
    action: string;
    owner: string;
    nonce: string; // Nonce as hex string (e.g., "0x1")
    timestamp: number; // Timestamp as number (Unix timestamp in seconds)
    deadline: string; // Deadline as hex string (e.g., "0x1234")
  };
  baseURL: string;
  username?: string;
  referralCode?: string;
  timestamp: number; // Timestamp for request body
}

export interface OnboardResult {
  success: boolean;
  authToken?: string;
  refreshToken?: string;
  expiresAt?: number;
  error?: string;
}

/**
 * Sign onboarding message using generateSingleSignature (EIP-712 format)
 * This uses the Universal Signature Generator API matching Go implementation
 *
 * CRITICAL: timestamp MUST be included in the EIP-712 signed payload.
 * The Go backend's VerifyWalletAuthSignature explicitly includes timestamp in payload verification.
 *
 * CRITICAL: Uses ethers v6 HDNodeWallet.fromPhrase() with explicit BIP44 path m/44'/60'/0'/0/0
 * This ensures the private key is derived using the same method as MetaMask, Go terminal,
 * and all major Ethereum wallets, ensuring secp256k1 curve compatibility.
 *
 * @param mnemonic - Mnemonic phrase to derive wallet from
 * @param walletAddress - Wallet address (for validation)
 * @param nonce - Nonce as hex string (e.g., "0x1")
 * @param timestamp - Timestamp as number (Unix timestamp in seconds)
 * @param deadline - Deadline as hex string (e.g., "0x1234")
 * @returns Signature string with 0x prefix
 */
export async function signOnboardMessage(
  mnemonic: string,
  walletAddress: string,
  nonce: string,
  timestamp: number,
  deadline: string,
): Promise<string> {
  // Derive wallet from mnemonic using ethers v6 HDNodeWallet with explicit BIP44 path
  // This matches MetaMask, Go terminal, and all major Ethereum wallets
  // Path: m/44'/60'/0'/0/0 (Ethereum standard BIP44 derivation)
  const masterNode = ethers.HDNodeWallet.fromPhrase(
    mnemonic.trim(),
    "", // no passphrase
    "m/44'/60'/0'/0/0", // Ethereum standard BIP44 path
  );

  // Validate that derived address matches provided address
  if (masterNode.address.toLowerCase() !== walletAddress.toLowerCase()) {
    throw new Error(
      `Address mismatch: derived ${masterNode.address} does not match provided ${walletAddress}`,
    );
  }

  // CRITICAL: Extract private key in the exact format Go's crypto.ToECDSA expects
  // Go's crypto.ToECDSA expects exactly 32 bytes, and the hex string must be:
  // - Exactly 64 lowercase hex characters
  // - No 0x prefix
  // - Valid secp256k1 private key (not zero, within curve order)

  // First, verify the private key is valid secp256k1 using ethers
  let signingKey: ethers.SigningKey;
  try {
    signingKey = new ethers.SigningKey(masterNode.privateKey);
  } catch (error: any) {
    throw new Error(
      `Invalid private key: cannot create signing key - ${error.message}`,
    );
  }

  // Verify the derived address matches (proves private key is correct)
  const derivedAddress = ethers.computeAddress(signingKey.publicKey);
  if (derivedAddress.toLowerCase() !== walletAddress.toLowerCase()) {
    throw new Error(
      `Private key validation failed: derived address ${derivedAddress} does not match ${walletAddress}`,
    );
  }

  // Extract private key bytes - CRITICAL: Match Go's privKey.D.Bytes() format exactly
  // Go's crypto.FromECDSA(priv) returns privKey.D.Bytes() where D is a *big.Int
  // The bytes may have leading zeros stripped, so we need to pad to exactly 32 bytes
  const privateKeyBigInt = BigInt(masterNode.privateKey);

  // Validate private key is within secp256k1 curve order FIRST (before encoding)
  // secp256k1 order: 0xFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFEBAAEDCE6AF48A03BBFD25E8CD0364141
  // Maximum valid private key: 0xFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFEBAAEDCE6AF48A03BBFD25E8CD0364140
  const SECP256K1_ORDER = BigInt(
    "0xFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFEBAAEDCE6AF48A03BBFD25E8CD0364141",
  );

  if (privateKeyBigInt === 0n || privateKeyBigInt >= SECP256K1_ORDER) {
    throw new Error(
      `Invalid private key: must be between 1 and secp256k1 order - 1`,
    );
  }

  // Convert BigInt to hex string (matches Go's privKey.D.Bytes() -> hex.EncodeToString)
  // BigInt.toString(16) gives us the hex representation without leading zeros
  // We MUST pad to exactly 64 hex chars (32 bytes) to match Go's format
  const hexWithoutPrefix = privateKeyBigInt.toString(16).toLowerCase();
  const paddedHex = hexWithoutPrefix.padStart(64, "0"); // Pad to 64 hex chars (32 bytes)

  // Convert hex string to bytes for validation
  const privateKeyBytes = ethers.getBytes("0x" + paddedHex);

  // Validate it's exactly 32 bytes (should always be true after padding)
  if (privateKeyBytes.length !== 32) {
    throw new Error(
      `Invalid private key: expected 32 bytes, got ${privateKeyBytes.length}`,
    );
  }

  // Validate private key is not zero (all zeros)
  const isZero = privateKeyBytes.every((byte) => byte === 0);
  if (isZero) {
    throw new Error("Invalid private key: cannot be zero");
  }

  // CRITICAL: Verify the bytes represent the same value as the BigInt
  // This ensures our encoding matches Go's behavior exactly
  const reconstructedBigInt = BigInt("0x" + paddedHex);
  if (reconstructedBigInt !== privateKeyBigInt) {
    throw new Error(
      `Private key encoding error: bytes do not match BigInt value. ` +
        `Original: ${privateKeyBigInt.toString(16)}, Reconstructed: ${reconstructedBigInt.toString(16)}`,
    );
  }

  // Convert to hex string WITHOUT 0x prefix (matches Go's hex.EncodeToString format exactly)
  // CRITICAL: Go's hex.EncodeToString(crypto.FromECDSA(priv)) produces:
  // - Lowercase hex
  // - No 0x prefix
  // - Exactly 64 chars for 32 bytes
  // The WASM hexToPrivateKey function removes 0x if present, but we match Go's exact format
  const privateKeyHex = paddedHex; // Already lowercase, no 0x prefix, exactly 64 chars

  // Final validation: ensure format is correct (must be exactly 64 chars, no 0x prefix)
  if (privateKeyHex.length !== 64) {
    throw new Error(
      `Invalid private key format: expected exactly 64 hex chars, got ${privateKeyHex.length}. ` +
        `This should never happen with a valid 32-byte private key.`,
    );
  }

  if (!/^[0-9a-f]{64}$/.test(privateKeyHex)) {
    throw new Error(
      `Invalid private key format: must be 64 lowercase hex characters (0-9, a-f) without 0x prefix`,
    );
  }

  const privateKeyHexFinal = privateKeyHex;

  // Build the payload matching Go implementation
  // CRITICAL: timestamp IS part of the EIP-712 signed payload for onboard action
  // Router's VerifyWalletAuthSignature explicitly includes timestamp in payload verification
  // The message must include: action, owner, nonce, timestamp, deadline (all 5 fields)
  const payload = {
    action: OperationTypeOnboard,
    owner: walletAddress.toLowerCase(),
    nonce: nonce,
    timestamp: timestamp,
    deadline: deadline,
  };

  // Build EIP712Tx structure for generateSingleSignature
  // This matches the structure expected by the Universal Signature Generator API
  const eip712Tx = {
    txId: "", // Empty txId as requested
    sigType: "ECDSA_LEGACY", // Signature type as string
    threshold: 1,
    timestamp: timestamp,
    payload: payload,
    deadline: parseInt(deadline, 16), // Convert hex deadline to number for EIP712Tx
  };

  console.log(
    "[Onboard] Signing EIP-712 typed data with generateSingleSignature:",
    eip712Tx,
  );

  // Debug: Log private key format validation
  console.log("[Onboard] Private key format validated:", {
    length: privateKeyHexFinal.length,
    format:
      "64 lowercase hex chars (no 0x prefix) - matches Go's hex.EncodeToString",
    preview: `${privateKeyHexFinal.substring(0, 8)}...${privateKeyHexFinal.substring(56)}`,
    isValidSecp256k1: true,
    withinCurveOrder: privateKeyBigInt < SECP256K1_ORDER,
    notZero: privateKeyBigInt !== 0n,
    bytesLength: privateKeyBytes.length,
    // TEMPORARY: Log full hex for debugging (remove after fix)
    fullHex: privateKeyHexFinal,
    firstByte: privateKeyHexFinal.substring(0, 2),
    lastByte: privateKeyHexFinal.substring(62, 64),
  });

  // Use generateSingleSignature to sign the EIP712Tx
  // Go WASM expects lowercase hex - use it directly (no fallback needed)
  console.log("[Onboard] Calling WASM GenerateSingleSignature with:", {
    txJSONLength: JSON.stringify(eip712Tx).length,
    privateKeyHexLength: privateKeyHexFinal.length,
    privateKeyHexPreview: privateKeyHexFinal,
    signerAddress: walletAddress.toLowerCase(),
  });

  const result = await mormSigner.generateSingleSignature(
    JSON.stringify(eip712Tx),
    privateKeyHexFinal,
    walletAddress.toLowerCase(),
  );
  console.log(result);

  // Remove the uppercase/lowercase fallback - not needed
  if (!result.success || !result.signature) {
    throw new Error(result.error || "Failed to generate signature");
  }

  console.log("[Onboard] signature:", result.signature);
  return result.signature;
}

/**
 * Sign message with mormSigner.generateSingleSignature and make HTTP request to /auth/v1/onboard
 * This function handles the signing and request submission part of onboarding
 *
 * IMPORTANT: Uses mormSigner.generateSingleSignature which uses the Universal Signature Generator API.
 * This provides the same signature generation pattern as Go's GenerateRouterWriteSignature.
 *
 * @param params - Onboarding request parameters
 * @returns OnboardResult with authToken if successful
 */
export async function submitOnboardRequest(
  params: OnboardRequestParams,
): Promise<OnboardResult> {
  try {
    // Sign the message using EIP-712 format (matching Go GenerateRouterWriteSignature)
    // CRITICAL: timestamp must be included in the signed payload
    const signature = await signOnboardMessage(
      params.mnemonic,
      params.walletAddress,
      params.message.nonce,
      params.message.timestamp,
      params.message.deadline,
    );

    console.log(
      "[Onboard] Signature generated successfully, length:",
      signature.length,
    );

    // Build request body per auth_api: address, signature, nonce, timestamp, deadline only.
    // username, referral_code, chain_type were removed in proto/API update; do not send.
    const requestBody: Record<string, any> = {
      address: params.walletAddress,
      signature: signature,
      nonce: params.message.nonce,
      timestamp: params.timestamp,
      deadline: params.message.deadline,
    };

    // Debug: Log request body (without full signature)
    console.log("[Onboard] Request body:", {
      ...requestBody,
      signature: signature.substring(0, 20) + "...",
    });

    // POST to /auth/v1/onboard
    const httpClient = new HttpClient(params.baseURL);
    const response = await httpClient.post("/auth/v1/onboard", requestBody);

    // Check for success
    if (response.statusCode === 200 && response.body) {
      // Extract tokens from response
      // Response format may vary, try common field names
      const authToken =
        response.body.access_token ||
        response.body.authToken ||
        response.body.token ||
        response.body.accessToken;

      const refreshToken =
        response.body.refresh_token || response.body.refreshToken;

      const expiresAt =
        response.body.expires_at ||
        response.body.expiresAt ||
        (response.body.expires_in
          ? params.timestamp + response.body.expires_in
          : undefined);

      if (authToken) {
        return {
          success: true,
          authToken,
          refreshToken,
          expiresAt,
        };
      } else {
        return {
          success: false,
          error: "Response missing authToken field",
        };
      }
    } else {
      // Handle error response - throw error so retry mechanism can catch it
      const errorMsg =
        response.body?.message ||
        response.body?.error ||
        `HTTP ${response.statusCode}: ${response.body}`;

      const error = new Error(errorMsg);
      // Attach response body for error detection
      (error as any).responseBody = response.body;
      (error as any).statusCode = response.statusCode;
      throw error;
    }
  } catch (error: any) {
    throw error;
  }
}
